<script setup>
/**
 * A Mystic in the search results (lesson-redo DESIGN.md C2; API.md §1):
 * a square 64px portrait (Mystics are squares), the name, a two-line snippet
 * with its matched words in gold bold, and one reason line (✦ Reads for
 * love). A tap emits `open` (the Mystic's page in the app; counted as an open).
 */
import { computed, ref } from 'vue'
import { snippetParts, whyLine } from '@/utils/community-lessons'

const props = defineProps({ mystic: { type: Object, required: true } })
const emit = defineEmits(['open'])
const parts = computed(() => snippetParts(props.mystic.snippet, props.mystic.match_words))
const why = computed(() => whyLine(props.mystic.why))
const failed = ref(false)
const initial = computed(() => String(props.mystic.name || '✦').trim().charAt(0).toUpperCase())
</script>

<template>
  <button type="button" class="mr" data-testid="mystic-result" @click="emit('open', mystic)">
    <span class="sq" aria-hidden="true">
      <img v-if="mystic.avatar && !failed" :src="mystic.avatar" alt="" @error="failed = true" />
      <span v-else class="sq-ph">{{ initial }}</span>
    </span>
    <span class="mr-t">
      <span class="nm">{{ mystic.name }}</span>
      <span v-if="parts.length" class="sn"><template v-for="(p, i) in parts" :key="i"><b v-if="p.hit">{{ p.text }}</b><template v-else>{{ p.text }}</template></template></span>
      <span v-if="why" class="why">{{ why }}</span>
    </span>
  </button>
</template>

<style scoped>
.mr {
  display: grid; grid-template-columns: 64px minmax(0, 1fr); gap: 14px; align-items: start; width: 100%; padding: 14px; border-radius: 16px; text-align: left; cursor: pointer;
  color: inherit; font: inherit; background: rgba(255, 255, 255, .04); border: 1px solid rgba(231, 201, 131, .25);
  transition: border-color .2s, background .2s, transform .2s; animation: mr-rise .35s cubic-bezier(.2, .8, .2, 1) both; box-sizing: border-box;
}
.mr:hover { border-color: rgba(240, 217, 163, .6); background: rgba(255, 255, 255, .07); transform: translateY(-1px); }
.mr:focus-visible { outline: 2px solid #e7c983; outline-offset: 2px; }
.sq { width: 64px; height: 64px; border-radius: 12px; overflow: hidden; border: 1px solid rgba(231, 201, 131, .55); box-shadow: 0 10px 20px -10px #000; background: #2a1845; display: block; }
.sq img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sq-ph { display: grid; place-items: center; width: 100%; height: 100%; font: 600 24px/1 'Letter Display', 'Cinzel', Georgia, serif; color: #e7c983; }
.mr-t { min-width: 0; }
.nm { display: block; font: 600 20px/1.15 'Letter Serif', 'Cormorant Garamond', Georgia, serif; color: #fff6e4; overflow-wrap: anywhere; }
.sn { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; overflow: hidden; margin-top: 3px; font: 500 13.5px/1.4 Inter, system-ui, sans-serif; color: #b9a6d6; }
.sn b { color: #f0d9a3; font-weight: 600; }
.why { display: block; margin-top: 6px; font: 600 11.5px Inter, system-ui, sans-serif; color: #e7c983; }
@media (max-width: 767px) {
  .mr { grid-template-columns: 52px minmax(0, 1fr); padding: 12px; }
  .sq { width: 52px; height: 52px; }
}
@keyframes mr-rise { from { opacity: 0; transform: translateY(10px); } }
@media (prefers-reduced-motion: reduce) { .mr { animation: none; transition: none; } .mr:hover { transform: none; } }
</style>
